<template>
  <SignupStepFrame
    :step-number="3"
    :step-total="5"
    eyebrow="Step 3 — Your team"
    title="Who&#39;s <b>coming with you</b>?"
    subtitle="Add your team and assign each person a manager so feedback flows to the right place. Up to 20 people on the free plan."
    :can-go-next="canGoNext"
    @next="onNext"
    @back="onBack"
    @saveAndExit="onSaveAndExit"
  >
    <TeamBuilder
      :value="people"
      :member-cap="memberCap"
      :signer-email="signerEmail"
      :company-name="companyName"
      @input="onPeopleChanged"
      @validity="onValidity"
    />

    <p v-if="overCapError" class="signup-error">
      You&#39;ve added {{ people.length }} people — the free plan is limited to {{ memberCap }}.
      Remove a few to continue.
    </p>
    <p v-else-if="hasInvalidRow" class="signup-error">
      One or more rows are incomplete. Each person needs a name, a valid email, a team, and a manager (except the one person at the top of the org).
    </p>
  </SignupStepFrame>
</template>

<script>
/**
 * WizardTeamsStep (step 4) — Phase D Track D2 rewrite.
 *
 * Wraps the new flat <TeamBuilder> people-table primitive and commits
 * back to the signup Vuex module via the `signup/SET_PEOPLE` mutation.
 * The signer is always pinned at index 0 (seeded by `verifyPin`), so
 * this step only has to forward edits.
 *
 * Validation:
 *  - Hard cap: total people <= 20.
 *  - Each non-signer row must have name, valid email, and a team.
 *  - Manager is encouraged but not required (defaults to "—").
 */
import SignupStepFrame from './SignupStepFrame.vue'
import TeamBuilder from './_primitives/TeamBuilder.vue'
import { mapGetters, mapState } from 'vuex'

const MEMBER_CAP = 20

export default {
  name: 'WizardTeamsStep',
  components: { SignupStepFrame, TeamBuilder },
  data() {
    return {
      memberCap: MEMBER_CAP,
      builderValid: true,
    }
  },
  computed: {
    ...mapGetters('signup', ['people', 'wizardData']),
    ...mapState('signup', {
      signerEmail: (s) => (s && s.email) || '',
    }),
    companyName() {
      return (this.wizardData && this.wizardData.company && this.wizardData.company.name) || ''
    },
    overCapError() {
      return Array.isArray(this.people) && this.people.length > this.memberCap
    },
    hasInvalidRow() {
      return !this.builderValid && !this.overCapError
    },
    canGoNext() {
      return this.builderValid && !this.overCapError
    },
  },
  methods: {
    onPeopleChanged(nextPeople) {
      this.$store.commit('signup/SET_PEOPLE', nextPeople)
    },
    onValidity(isValid) {
      this.builderValid = !!isValid
    },
    onNext() {
      if (!this.canGoNext) return
      this.$emit('next')
    },
    onBack() {
      this.$emit('back')
    },
    onSaveAndExit() {
      this.$emit('saveAndExit')
    },
  },
}
</script>
